/**
 * Compact object-level Activity timeline.
 *
 * Renders the rows returned by ``useExceptionObjectHistory`` as a single
 * connected rail of events spanning EVERY visible code on the object (no
 * canonical collapse - a prior resolved -> re_detected cycle shows both
 * events). Each event: a state-coloured icon on the rail, a muted verb prefix
 * (Detected / Resolved / ...) leading the bold issue type, a right-aligned
 * timestamp, then optional actor + reason lines. The issue type leads every
 * row (single- and multi-code objects alike) and wraps rather than truncating.
 * Events from one bulk action (same kind / time / actor / reason) are folded
 * into a single entry that lists every affected issue.
 *
 * Colour means state only - icon colours single-source from the same status
 * FGs the Issues rows use. Verbs / timestamps stay neutral.
 */
import React, { useState } from 'react'
import { Box, Button, Tooltip, Typography } from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined'
import type { TimelineEvent } from '../../../hooks/useExceptionObjectHistory'
import { formatRelativeTimestamp } from '../../../utils/dateFormatters'
import { poolwerxColors } from '../../../theme/colors'
import {
  WARNING_FG,
  BLOCKED_FG,
  MARKED_FIXED_FG,
  FIX_NOT_CONFIRMED_FG,
} from '../../../config/exceptions/shared/issuesState'

/**
 * Display-side event taxonomy: the backend kinds plus the frontend-derived
 * ``fix_not_confirmed`` ("marked fixed, but a later sync still detected the
 * issue" - built from the open entries' ``fix_confirmation_state``, no
 * backend event exists for it yet). Full audit fidelity (unblocked /
 * unmarked / reopened / note-added) lands with the activity-feed follow-up.
 */
export type DisplayTimelineEventKind =
  | TimelineEvent['kind']
  | 'fix_not_confirmed'
export type DisplayTimelineEvent = Omit<TimelineEvent, 'kind'> & {
  kind: DisplayTimelineEventKind
}

const KIND_LABEL: Record<DisplayTimelineEventKind, string> = {
  detected: 'Detected issue',
  re_detected: 'Re-detected after previous resolution',
  blocked: 'Blocked',
  resolved: 'Resolved by sync',
  ignored: 'Ignored',
  marked_fixed: 'Marked fixed - awaiting sync',
  fix_not_confirmed: 'Fix not confirmed by sync',
}

// State -> icon colour, single-sourced to the Issues-row status FGs so the
// timeline reads the same as the rows (amber detected, red blocked, neutral
// ignored). Green is reserved for sync-confirmed RESOLVED only - marked_fixed
// is teal (the actioned event) and fix_not_confirmed red-orange (the sync
// still saw it).
const KIND_COLOR: Record<DisplayTimelineEventKind, string> = {
  detected: WARNING_FG,
  re_detected: WARNING_FG,
  blocked: BLOCKED_FG,
  resolved: poolwerxColors.variantGreen,
  ignored: poolwerxColors.text.tertiary,
  marked_fixed: MARKED_FIXED_FG,
  fix_not_confirmed: FIX_NOT_CONFIRMED_FG,
}

const KindIcon: React.FC<{ kind: DisplayTimelineEventKind }> = ({ kind }) => {
  const props = { sx: { fontSize: 16, color: KIND_COLOR[kind] } }
  switch (kind) {
    case 'detected':
      return <VisibilityIcon {...props} />
    case 're_detected':
      return <RestartAltIcon {...props} />
    case 'blocked':
      return <LockOutlinedIcon {...props} />
    case 'resolved':
      return <CheckCircleOutlineIcon {...props} />
    case 'ignored':
      return <VisibilityOffIcon {...props} />
    case 'marked_fixed':
      return <AssignmentTurnedInOutlinedIcon {...props} />
    case 'fix_not_confirmed':
      return <AssignmentLateOutlinedIcon {...props} />
  }
}

const REASON_PREVIEW_MAX = 80

const truncate = (s: string, n: number): string =>
  s.length <= n ? s : `${s.slice(0, n - 1)}…`

// A single bulk action (e.g. "Can't fix yet" across several issues) writes one
// event per affected tag, all sharing kind / timestamp / actor / reason. Fold
// adjacent same-action events into one entry that lists every affected issue,
// so the feed reads as one action rather than N near-identical rows.
interface TimelineItem {
  code: string
  name: string
  tag_id: number
}
interface TimelineGroup {
  kind: DisplayTimelineEventKind
  occurred_at: string
  actor: TimelineEvent['actor']
  reason: string | null
  items: TimelineItem[]
}

const actorKey = (a: TimelineEvent['actor']): string =>
  a ? `${a.id ?? ''}|${a.email ?? ''}|${a.name ?? ''}` : ''

const groupKey = (e: DisplayTimelineEvent): string =>
  `${e.kind}|${e.occurred_at}|${e.reason ?? ''}|${actorKey(e.actor)}`

const groupAdjacent = (events: DisplayTimelineEvent[]): TimelineGroup[] => {
  const groups: TimelineGroup[] = []
  let lastKey = ''
  for (const e of events) {
    const item = { code: e.code, name: e.name, tag_id: e.tag_id }
    const key = groupKey(e)
    if (groups.length && key === lastKey) {
      groups[groups.length - 1].items.push(item)
    } else {
      groups.push({
        kind: e.kind,
        occurred_at: e.occurred_at,
        actor: e.actor,
        reason: e.reason,
        items: [item],
      })
      lastKey = key
    }
  }
  return groups
}

export interface StatusHistoryTimelineProps {
  events: DisplayTimelineEvent[]
  loading?: boolean
  error?: string | null
  /** The drawer's focused code - its chip is bolded so the user can locate the
   *  code they opened in the feed (only shown when the object is multi-code). */
  currentCode?: string
}

// Show the most recent events by default; older ones fold behind a
// "Show full history" toggle so a long-lived tuple doesn't force an
// internal scrollbar (the drawer body scrolls instead).
const DEFAULT_VISIBLE = 4

const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({
  events,
  loading,
  error,
  currentCode,
}) => {
  const [showAll, setShowAll] = useState(false)
  if (loading) {
    return (
      <Typography variant="caption" color="text.secondary">
        Loading history…
      </Typography>
    )
  }
  if (error) {
    return (
      <Typography variant="caption" sx={{ color: poolwerxColors.error }}>
        {error}
      </Typography>
    )
  }
  if (!events.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        No lifecycle events yet.
      </Typography>
    )
  }

  // Reverse-chronological (latest first), matching the notes thread above.
  // Backend returns ASC by occurred_at; flipping client-side keeps the shared
  // API contract untouched. Fold bulk actions into single entries afterwards.
  const ordered = groupAdjacent([...events].reverse())
  const hiddenCount = ordered.length - DEFAULT_VISIBLE
  const collapsed = !showAll && hiddenCount > 0
  const visibleGroups = collapsed ? ordered.slice(0, DEFAULT_VISIBLE) : ordered

  return (
    <Box>
      <Box
        component="ol"
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          paddingInlineStart: 0,
        }}
      >
        {visibleGroups.map((group, idx, arr) => {
          const isLast = idx === arr.length - 1
          const actorName = group.actor?.name || group.actor?.email
          const reasonPreview = group.reason
            ? truncate(group.reason, REASON_PREVIEW_MAX)
            : null
          const ts = formatRelativeTimestamp(group.occurred_at)
          return (
            <Box
              key={`${group.kind}-${group.occurred_at}-${idx}`}
              component="li"
              sx={{ display: 'flex', gap: '12px', alignItems: 'stretch' }}
            >
              {/* Left rail: the event icon on a surface-coloured backing so the
                * connector breaks cleanly behind it, then a thin line down to
                * the next event. The last event has no connector. */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flexShrink: 0,
                  width: 18,
                }}
                aria-hidden="true"
              >
                <Box
                  sx={{
                    mt: '1px',
                    lineHeight: 0,
                    bgcolor: poolwerxColors.surface.primary,
                  }}
                >
                  <KindIcon kind={group.kind} />
                </Box>
                {!isLast && (
                  <Box
                    sx={{
                      flex: 1,
                      width: '2px',
                      bgcolor: poolwerxColors.border.secondary,
                      mt: '2px',
                      minHeight: 12,
                    }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  // Owns the inter-event spacing + gives the rail connector
                  // something to stretch through. Zero on the last row.
                  pb: isLast ? 0 : '20px',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'baseline',
                    gap: 1,
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '13.5px',
                      color: poolwerxColors.text.primary,
                      minWidth: 0,
                      lineHeight: 1.4,
                    }}
                  >
                    {/* Muted verb prefix - the action, de-emphasised so the
                      * issue type reads as the row's lead. */}
                    <Box
                      component="span"
                      sx={{
                        fontWeight: 500,
                        color: poolwerxColors.text.tertiary,
                        mr: 0.625,
                      }}
                    >
                      {KIND_LABEL[group.kind]}
                    </Box>
                    {/* Affected issue type(s) - the bold lead. A bulk action
                      * lists every issue it touched, comma-separated. Each wraps
                      * rather than truncating; full text on hover; the code the
                      * drawer was opened from is bolder still. */}
                    {group.items.map((it, i) => (
                      <React.Fragment key={it.tag_id}>
                        {i > 0 && (
                          <Box
                            component="span"
                            sx={{ color: poolwerxColors.text.tertiary }}
                          >
                            {', '}
                          </Box>
                        )}
                        <Box
                          component="span"
                          title={
                            it.code === currentCode
                              ? `${it.name || it.code} (you are here)`
                              : it.name || it.code
                          }
                          sx={{
                            fontWeight: it.code === currentCode ? 700 : 600,
                            wordBreak: 'break-word',
                          }}
                        >
                          {it.name || it.code}
                        </Box>
                      </React.Fragment>
                    ))}
                  </Typography>
                  <Tooltip title={ts.title} arrow>
                    <Typography
                      component="time"
                      dateTime={ts.dateTime}
                      sx={{
                        fontSize: '11.5px',
                        color: poolwerxColors.text.tertiary,
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        cursor: 'help',
                      }}
                    >
                      {ts.label}
                    </Typography>
                  </Tooltip>
                </Box>
                {actorName && (
                  <Typography
                    sx={{
                      mt: '2px',
                      fontSize: '12.5px',
                      color: poolwerxColors.text.secondary,
                    }}
                  >
                    by {actorName}
                  </Typography>
                )}
                {reasonPreview && (
                  <Tooltip title={group.reason || ''} placement="top-start">
                    <Typography
                      sx={{
                        mt: '3px',
                        fontSize: '12.5px',
                        color: poolwerxColors.text.tertiary,
                        lineHeight: 1.35,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600 }}>
                        Reason:
                      </Box>{' '}
                      {reasonPreview}
                    </Typography>
                  </Tooltip>
                )}
              </Box>
            </Box>
          )
        })}
      </Box>
      {hiddenCount > 0 && (
        <Button
          size="small"
          variant="text"
          onClick={() => setShowAll((v) => !v)}
          sx={{
            mt: 0.5,
            ml: '30px', // align under the event text (icon rail + gap)
            px: 0.5,
            minWidth: 0,
            textTransform: 'none',
            fontSize: '0.74rem',
            color: poolwerxColors.teal,
          }}
        >
          {showAll ? 'Show less' : `Show full history (${ordered.length})`}
        </Button>
      )}
    </Box>
  )
}

export default StatusHistoryTimeline
